import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ToolbarModule } from 'primeng/toolbar';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { UsersService } from 'src/app/private/services/users.service';
import { DepartMentService } from 'src/app/private/services/department.service';
import { RoleService } from 'src/app/private/services/role.service';
import { CenterService } from 'src/app/private/services/center.service';
import { CreateUserComponent } from "./create-user/create-user.component";
import { DeleteUserComponent } from 'src/app/private/components/user-section/delete-user/delete-user.component';

@Component({
  selector: 'app-user-section',
  standalone: true,
  imports: [
    SharedModule,
    CreateUserComponent,
    DeleteUserComponent
],
  templateUrl: './user-section.component.html',
})
export class UserSectionComponent {
  @Input() clientId!: string;
  users: any[] = [];
  totalRecords = 0;
  rows = 10;
  loading = false;
  showModal = false;
  isSaving = false;
  searchText = '';
  activeSection = 'details';
  actionItems: any[] = [];
  centers: any[] = [];
  departments: any[] = [];
  roles: any[] = [];

  userForm!: FormGroup;

  userDataToEdit: any = null; // To hold the user data to be edited
  showEditUser: boolean = false; // Flag to show the edit user modal
  showDeleteUser: boolean = false; // Flag to show the delete user modal

  userActionItem: any[] = [
    {
      label: 'Options',
      items: [
        {
          label: 'Edit User',
          icon: 'pi pi-fw pi-pencil',
          command: () => {
            this.showEditUser = true;
          }
        },
        {
          label: 'Delete User',
          icon: 'pi pi-fw pi-trash',
          styleClass: 'delete-menu-item',
          command: () => {
            this.showDeleteUser = true;
          }
        }
      ]
    }
  ]

  constructor(private fb: FormBuilder, private userService: UsersService,
    private deptService: DepartMentService, private roleService: RoleService,
    private centerService: CenterService
  ) { }

  ngOnInit() {
    this.userForm = this.fb.group({
      prefix: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      gender: [''],
      professionalDetails: [''],
      center: [''],
      department: [''],
      role: [''],
      status: [''],
      username: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      twoFA: [''],
    });

    this.loadUsers();
    this.getAllDepartments();
    this.getAllRoles();
    this.getAllCenters();
  }

  loadUsers(event?: any) {
    const page = event?.first * event?.rows || 0; // Calculate the page number based on the event
    const limit = event?.rows || this.rows;

    this.loading = true;

    const payload = {
      "page": 0,
      "limit": 200,
      "where": {
        "client_id": this.clientId,
      },
    
    }
    this.userService.getAllUsers(payload).subscribe({
      next: (res: any) => {
        this.users = res.rows || [];
        this.totalRecords = res.count || 0;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  reloadUsers() {
    this.loadUsers({ first: 0, rows: this.rows });
  }

  getAllDepartments() {
    const payload = {
      page: 0,
      limit: 100,
      where: {
        client_id: this.clientId
      },
      order: {
        col: '"department"."updatedAt"',
        dir: 'DESC'
      }
    };
    this.deptService.getDeptWithCount(payload).subscribe({
      next: (res) => {
        this.departments = res?.rows || [];
      },
      error: () => { }
    });
  }

  getAllRoles() {
    const payload = {
      "page": 0,
      "limit": 100,
      "where": {
        "client_id": this.clientId,
      },
      "order": {
        "col": "\"role\".\"updatedAt\"",
        "dir": "DESC"
      }
    }
    this.roleService.getAllRole(payload).subscribe({
      next: (res) => {
        this.roles = res?.rows || [];
      },
      error: () => { }
    });
  }

  getAllCenters() {
    const payload = {
      "page": 0,
      "limit": 100,
      "where": {
        "client_id": this.clientId,
      },
      "order": {
        "col": "\"center\".\"updatedAt\"",
        "dir": "DESC"
      }
    }

    this.centerService.getAllCenterWithCount(payload).subscribe({
      next: (res) => {
        this.centers = res?.rows || [];
      },
      error: () => { }
    });
  }

  handleSearch(value: string) {
    this.searchText = value;
    this.loadUsers();
  }

  openAddModal(user?: any) {
    this.userForm.reset();
    if (user) this.userForm.patchValue(user);
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  saveUser() {
    if (this.userForm.invalid) return;

    this.isSaving = true;
    const payload = this.userForm.value;
    this.userService.createUser(payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.showModal = false;
        this.loadUsers();
      },
      error: () => this.isSaving = false,
    });
  }

  clearForm() {
    this.userForm.reset();
  }

  setUserDataToEdit(user: any) {
    this.userDataToEdit = user;
  }
}
